/**
 * Share Report Modal Component
 * 
 * Quick share modal for proposals in review workflow
 * Based on ShareReportModal pattern from Minestracker
 */

import React, { useState } from 'react';
import { Modal, Button, Input, useToastStore } from '@tracekit/core';
import { shareReport } from '@tracekit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Share2, X, UserPlus } from 'lucide-react';

interface ShareReportModalProps {
  show: boolean;
  onHide: () => void;
  reportId: string;
  reportName?: string;
}

export const ShareReportModal: React.FC<ShareReportModalProps> = ({
  show,
  onHide,
  reportId,
  reportName = 'Report',
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [permission, setPermission] = useState<'view' | 'edit' | 'admin'>('view');
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();

  const shareMutation = useMutation({
    mutationFn: ({ emails, permission }: { emails: string[]; permission: 'view' | 'edit' | 'admin' }) =>
      shareReport(reportId, emails, permission),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-shares', reportId] });
      addToast({
        message: `Report shared with ${emails.length} user(s)`,
        type: 'success',
      });
      handleClose();
    },
    onError: (error: any) => {
      addToast({
        message: error.message || 'Failed to share report',
        type: 'error',
      });
    },
  });

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleAddEmail = (e: React.KeyboardEvent | React.MouseEvent) => {
    if (e.type === 'keydown' && (e as React.KeyboardEvent).key !== 'Enter') {
      return;
    }
    e.preventDefault();

    const trimmedEmail = emailInput.trim().toLowerCase();

    if (!trimmedEmail) return;

    if (!isValidEmail(trimmedEmail)) {
      addToast({
        message: 'Please enter a valid email address',
        type: 'error',
      });
      return;
    }

    if (emails.includes(trimmedEmail)) {
      addToast({
        message: 'Email already added',
        type: 'error',
      });
      return;
    }

    setEmails((prev) => [...prev, trimmedEmail]);
    setEmailInput('');
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails((prev) => prev.filter((e) => e !== emailToRemove));
  };

  const handleShare = () => {
    if (emails.length === 0) {
      addToast({
        message: 'Please add at least one email address',
        type: 'error',
      });
      return;
    }

    if (!reportId) {
      addToast({
        message: 'Report ID is required',
        type: 'error',
      });
      return;
    }

    shareMutation.mutate({ emails, permission });
  };

  const handleClose = () => {
    setEmails([]);
    setEmailInput('');
    setPermission('view');
    onHide();
  };

  return (
    <Modal isOpen={show} onClose={handleClose} title="Share Report" size="md">
      <div className="space-y-4">
        {/* Report Name */}
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <small className="text-gray-600 dark:text-gray-400 font-semibold">Sharing:</small>
          <div className="font-semibold text-gray-900 dark:text-gray-100">{reportName}</div>
        </div>

        {/* Email Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Email Addresses
          </label>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Enter email address"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleAddEmail}
              disabled={shareMutation.isPending}
              className="flex-1"
            />
            <Button
              onClick={handleAddEmail}
              disabled={!emailInput.trim() || shareMutation.isPending}
              variant="outline"
            >
              <UserPlus className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">Press Enter or click + to add email</p>
        </div>

        {/* Email List */}
        {emails.length > 0 && (
          <div>
            <small className="text-gray-600 dark:text-gray-400 font-semibold mb-2 block">
              Sharing with ({emails.length}):
            </small>
            <div className="flex flex-wrap gap-2">
              {emails.map((email, index) => (
                <div
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium border border-blue-200 dark:border-blue-800"
                >
                  {email}
                  <button
                    onClick={() => handleRemoveEmail(email)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Permission Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Permission Level
          </label>
          <div className="flex gap-2">
            {(['view', 'edit', 'admin'] as const).map((perm) => (
              <button
                key={perm}
                onClick={() => setPermission(perm)}
                disabled={shareMutation.isPending}
                className={`flex-1 px-4 py-2 border-2 rounded-lg transition-all ${
                  permission === perm
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="capitalize font-medium text-sm">{perm}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={shareMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleShare}
            disabled={emails.length === 0 || shareMutation.isPending}
          >
            {shareMutation.isPending ? (
              <>Sharing...</>
            ) : (
              <>
                <Share2 className="w-4 h-4 mr-2" />
                Share Report
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

